import type { AlertRule, AlertContext } from '../types/alerts';

// ============================================================================
// ALERT DETECTION RULES
// ============================================================================

export const ALERT_RULES: AlertRule[] = [
  {
    id: 'critical-path-delay',
    name: 'Retard sur le Chemin Critique',
    description: 'Détecte les tâches du chemin critique en retard',
    category: 'schedule',
    enabled: true,
    severity: 'critical',
    cooldownMinutes: 60,
    condition: (context: AlertContext) => {
      // Check if any critical path nodes are behind schedule
      const criticalNodes = context.nodes.filter((node: any) =>
        context.criticalPath.includes(node.id)
      );

      return criticalNodes.some((node: any) => {
        if (!node.progress) return false;
        if (node.progress === 100) return false; // Completed tasks can't be late

        // Simple heuristic: if a task is less than 50% done but more than 50% of time has passed
        const timeElapsed = node.earlyFinish > 0 ? node.earlyStart / node.earlyFinish : 0;
        return node.progress < 50 && timeElapsed > 0.5;
      });
    },
  },

  {
    id: 'approaching-deadline',
    name: 'Échéance Proche',
    description: 'Alerte quand une tâche approche de son échéance (< 3 jours)',
    category: 'schedule',
    enabled: true,
    severity: 'warning',
    threshold: 3,
    cooldownMinutes: 1440, // 24 hours
    condition: (context: AlertContext) => {
      const threeDaysFromNow = new Date();
      threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

      // Check Kanban cards with due dates
      if (context.kanbanCards) {
        return context.kanbanCards.some((card: any) => {
          if (!card.dueDate || card.status === 'done') return false;
          const dueDate = new Date(card.dueDate);
          return dueDate <= threeDaysFromNow && dueDate > context.currentDate;
        });
      }

      return false;
    },
  },

  {
    id: 'overallocated-resource',
    name: 'Ressource Sur-allouée',
    description: 'Détecte les membres assignés à trop de tâches simultanément',
    category: 'resource',
    enabled: true,
    severity: 'warning',
    threshold: 5,
    cooldownMinutes: 360, // 6 hours
    condition: (context: AlertContext) => {
      const memberTaskCounts: Record<string, number> = {};

      // Count active tasks per member (from Kanban)
      if (context.kanbanCards) {
        context.kanbanCards.forEach((card: any) => {
          if (card.status !== 'done' && card.status !== 'backlog' && card.assignedTo) {
            card.assignedTo.forEach((memberId: string) => {
              memberTaskCounts[memberId] = (memberTaskCounts[memberId] || 0) + 1;
            });
          }
        });
      }

      // Check if any member exceeds threshold (5 tasks)
      return Object.values(memberTaskCounts).some((count) => count > 5);
    },
  },

  {
    id: 'high-risk-unmitigated',
    name: 'Risque Élevé Non Traité',
    description: 'Alerte sur les risques critiques sans plan de mitigation',
    category: 'risk',
    enabled: true,
    severity: 'critical',
    cooldownMinutes: 720, // 12 hours
    condition: (context: AlertContext) => {
      if (!context.risks || context.risks.length === 0) return false;

      // Check for high-severity risks without mitigation strategies
      return context.risks.some((risk: any) => {
        const score = risk.probability * risk.impact;
        const isHighRisk = score >= 15; // High risk threshold
        const hasMitigation = risk.mitigationStrategies && risk.mitigationStrategies.trim().length > 0;
        const isActive = risk.status === 'identified' || risk.status === 'in_treatment';

      return isHighRisk && !hasMitigation && isActive;
      });
    },
  },

  {
    id: 'blocked-tasks',
    name: 'Tâches Bloquées',
    description: 'Détecte les tâches bloquées depuis plus de 48h',
    category: 'dependency',
    enabled: true,
    severity: 'warning',
    threshold: 48,
    cooldownMinutes: 480, // 8 hours
    condition: (context: AlertContext) => {
      if (!context.kanbanCards) return false;

      const fortyEightHoursAgo = new Date();
      fortyEightHoursAgo.setHours(fortyEightHoursAgo.getHours() - 48);

      // Check for blocked cards that haven't been updated in 48h
      return context.kanbanCards.some((card: any) => {
        if (!card.blockedBy || card.blockedBy.length === 0) return false;
        const lastUpdate = card.updatedAt ? new Date(card.updatedAt) : new Date(card.createdAt);
        return lastUpdate < fortyEightHoursAgo;
      });
    },
  },

  {
    id: 'low-team-velocity',
    name: 'Vélocité Équipe Faible',
    description: 'Alerte si le taux de complétion des tâches diminue',
    category: 'performance',
    enabled: true,
    severity: 'warning',
    threshold: 30,
    cooldownMinutes: 1440, // 24 hours
    condition: (context: AlertContext) => {
      // Check completion rate from statistics
      const completionRate = context.statistics?.completionPercentage || 0;
      return completionRate < 30 && context.nodes.length > 5; // Only alert if there are enough tasks
    },
  },

  {
    id: 'wip-limit-exceeded',
    name: 'Limite WIP Dépassée',
    description: 'Détecte le dépassement des limites Work In Progress',
    category: 'performance',
    enabled: true,
    severity: 'warning',
    cooldownMinutes: 240, // 4 hours
    condition: (context: AlertContext) => {
      if (!context.kanbanCards) return false;

      // Count cards in 'in_progress' status
      const inProgressCount = context.kanbanCards.filter(
        (card: any) => card.status === 'in_progress'
      ).length;

      // Default WIP limit is 5
      const wipLimit = 5;
      return inProgressCount > wipLimit;
    },
  },

  {
    id: 'unassigned-tasks',
    name: 'Tâches Non Assignées',
    description: 'Alerte sur les tâches critiques sans assignation',
    category: 'team',
    enabled: true,
    severity: 'warning',
    cooldownMinutes: 720, // 12 hours
    condition: (context: AlertContext) => {
      if (!context.kanbanCards) return false;

      // Check for urgent/critical priority tasks without assignees
      return context.kanbanCards.some((card: any) => {
        const isUrgent = card.priority === 'urgent' || card.priority === 'high';
        const isNotDone = card.status !== 'done' && card.status !== 'backlog';
        const isUnassigned = !card.assignedTo || card.assignedTo.length === 0;

        return isUrgent && isNotDone && isUnassigned;
      });
    },
  },

  {
    id: 'project-behind-schedule',
    name: 'Projet en Retard',
    description: 'Alerte si la date de fin projetée dépasse la date prévue',
    category: 'schedule',
    enabled: true,
    severity: 'critical',
    cooldownMinutes: 1440, // 24 hours
    condition: (context: AlertContext) => {
      if (!context.projectEndDate || !context.statistics?.projectedEndDate) return false;

      const plannedEnd = new Date(context.projectEndDate);
      const projectedEnd = new Date(context.statistics.projectedEndDate);

      return projectedEnd > plannedEnd;
    },
  },

  {
    id: 'no-recent-updates',
    name: 'Absence de Mises à Jour',
    description: 'Alerte si aucune tâche n\'a été mise à jour depuis 5 jours',
    category: 'performance',
    enabled: true,
    severity: 'warning',
    cooldownMinutes: 2880, // 48 hours
    condition: (context: AlertContext) => {
      if (!context.kanbanCards || context.kanbanCards.length === 0) return false;

      const fiveDaysAgo = new Date();
      fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);

      // Check if ALL cards have not been updated in 5 days
      const allStale = context.kanbanCards.every((card: any) => {
        const lastUpdate = card.updatedAt ? new Date(card.updatedAt) : new Date(card.createdAt);
        return lastUpdate < fiveDaysAgo;
      });

      return allStale;
    },
  },
];

// Helper function to create an alert from a triggered rule
export const createAlertFromRule = (
  rule: AlertRule,
  _context: AlertContext,
  details?: string
): Omit<any, 'id' | 'detectedAt' | 'priority'> => {
  // Calculate impact and urgency based on rule category and severity
  let impact = 5;
  let urgency = 5;

  switch (rule.severity) {
    case 'critical':
      impact = 9;
      urgency = 9;
      break;
    case 'warning':
      impact = 6;
      urgency = 6;
      break;
    case 'info':
      impact = 3;
      urgency = 3;
      break;
    case 'success':
      impact = 2;
      urgency = 2;
      break;
  }

  // Determine suggested actions based on rule
  let suggestedActions: string[] = [];
  switch (rule.id) {
    case 'critical-path-delay':
      suggestedActions = [
        'Réallouer des ressources aux tâches critiques',
        'Organiser une réunion d\'urgence avec l\'équipe',
        'Revoir le planning et identifier les tâches compressibles',
      ];
      break;
    case 'approaching-deadline':
      suggestedActions = [
        'Vérifier l\'avancement des tâches concernées',
        'Prévenir les membres assignés',
        'Préparer un plan de contingence',
      ];
      break;
    case 'overallocated-resource':
      suggestedActions = [
        'Redistribuer certaines tâches',
        'Prioriser les tâches les plus critiques',
        'Envisager d\'ajouter des ressources supplémentaires',
      ];
      break;
    case 'high-risk-unmitigated':
      suggestedActions = [
        'Élaborer une stratégie de mitigation',
        'Assigner un responsable au risque',
        'Planifier des actions préventives',
      ];
      break;
    case 'blocked-tasks':
      suggestedActions = [
        'Identifier et résoudre les blocages',
        'Contacter les responsables des dépendances',
        'Envisager des solutions alternatives',
      ];
      break;
  }

  return {
    title: rule.name,
    message: details || rule.description,
    severity: rule.severity,
    category: rule.category,
    status: 'active',
    suggestedActions,
    impact,
    urgency,
  };
};
